import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MyBetsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ThemeInverse } from '@frontend/sports/common/ui/shared';
import {
    SportsThemedNotificationDirective,
    ThemeNotificationSize,
    ThemeNotificationVariant,
} from '@frontend/sports/common/ui/sports-themed-notifcation-bubble';
import { SportsThemedTabsDirective, ThemeSize } from '@frontend/sports/common/ui/sports-themed-tab-group';
import { ComponentProxy, ComponentProxyDirective } from '@frontend/sports/deferred/feature/component-loader';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { OpenBetsSummary } from '@frontend/sports/types/models/my-bets';
import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { DsTabsGroup, DsTabsModule } from '@frontend/ui/tabsgroup';
import { Store } from '@ngrx/store';
import { betslipBaseStateSelector } from 'packages/sports/common/betslip/base/store/selectors';
import { IBetslipRootState } from 'packages/sports/common/betslip/base/store/state';
import { BetslipState } from 'packages/sports/common/betslip/core/betslip-state';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';
import { BetslipResultActions } from 'packages/sports/common/betslip/modules/result/actions';
import DigitalSettingsActions from 'packages/sports/common/betslip/modules/settings/digital-actions';
import { Observable, combineLatest, distinctUntilChanged, filter, map, startWith, take } from 'rxjs';

import { BetslipDigitalComponentLoaderService, RightColumnBinding } from '../betslip/betslip-digital-component-loader.service';
import { MyBetsSummaryService } from '../my-bets-base/my-bets-summary.service';
import { MyBetsComponentLoaderService } from '../my-bets/my-bets-component-loader.service';
import { StakeInputModeStrategy, TypingStakeInputModeStrategy } from '../numpad/stake-input-mode.strategy';
import { IUiRootState, uiStateSelector } from '../ui-manager/ui-manager.state';
import { UiStates } from '../ui-manager/ui-states.enum';
import { WidgetComponent } from '../widget/core/widget-registry.service';
import { BetColumnType } from './bet-column-type';

@Component({
    selector: 'ms-bet-column',
    templateUrl: 'bet-column.component.html',
    styleUrls: [`bet-column.component.scss`],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: StakeInputModeStrategy, useClass: TypingStakeInputModeStrategy }],
    imports: [DsTabsModule, SportsThemedTabsDirective, ComponentProxyDirective, AsyncPipe, DsNotificationBubble, SportsThemedNotificationDirective],
})
export class BetColumnComponent extends WidgetComponent<never> implements OnInit {
    @HostBinding('class') className = 'bet-column';
    @ViewChild(DsTabsGroup) tabGroup: DsTabsGroup;
    active = signal('');
    tabSize: ThemeSize;

    constructor(
        private sitecore: Sitecore,
        private myBetsSummary: MyBetsSummaryService,
        private dispatcher: DispatcherService,
        private routerEventsService: RouterEventsService,
        private trackingService: TrackingService,
        loader: MyBetsComponentLoaderService,
        betslipLoader: BetslipDigitalComponentLoaderService,
        private store: Store<IBetslipRootState & IUiRootState>,
        private betslipIntegrationService: BetslipIntegrationService,
        private mybetsConfig: MyBetsConfig,
    ) {
        super();

        this.myBetsComponent = loader.getColumnViewComponent();
        this.betslipComponent = betslipLoader.getBetslipComponent();
        this.editbetComponent = betslipLoader.getEditBetComponent();

        this.uiStates$ = this.store.select(uiStateSelector).pipe(map((ui) => ui.states));
        this.betslipBaseState$ = this.store.select(betslipBaseStateSelector);
        this.tabSize = {
            'betboo-br': 'small',
            'sportingbet-br': 'small',
        };
    }

    uiStates$: Observable<UiStates[]>;

    betColumnType = BetColumnType;

    variant: ThemeNotificationVariant = {
        'default': 'neutral',
        'mgm-4': 'utility',
        'sports-interaction': 'utility',
    };

    selectedTabBubbleVariant: ThemeNotificationVariant = {
        'default': 'primary',
        'mgm-4': 'utility',
        'sports-interaction': 'utility',
    };

    counterSize: ThemeNotificationSize = {
        'mgm-4': 'large',
        'sports-interaction': 'large',
    };

    myBetsCountBadgeText: string;
    isShowOpenBetsCount: boolean;
    myBetsComponent: ComponentProxy;
    betslipComponent: ComponentProxy<RightColumnBinding>;
    editbetComponent: ComponentProxy<RightColumnBinding>;

    private betslipBaseState$: Observable<BetslipState>;

    vm$: Observable<{
        isEditBet: boolean;
        betslipPickCount: number;
        myBetsCount: string | undefined;
        hideMyBets: boolean;
    }>;

    themeInverseIndicator: ThemeInverse = {
        'sports-interaction': { isInverse: false },
        'bwin-dark': { isInverse: false },
        'premium-dark': { isInverse: false },
        'mgm-4': { isInverse: false },
        'sportingbet-2': { isInverse: false },
        'betboo-br': { isInverse: false },
        'sportingbet-br': { isInverse: false },
    };

    get betslipTitle() {
        return this.sitecore.betslip.messages.Title;
    }

    get myBetsTitle() {
        return this.sitecore.globalMessages.MyBetsTitle;
    }

    ngOnInit(): void {
        this.betslipComponent.update({ inColumn: true });
        this.editbetComponent.update({ inColumn: true });
        this.isShowOpenBetsCount = this.mybetsConfig.isShowOpenBetsCount;
        this.active.set(this.betColumnType.Betslip.toString());

        this.betslipIntegrationService
            .betslipPicksAndLegsCount$()
            .pipe(
                distinctUntilChanged(),
                filter((count) => count > 0),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                if (!this.tabGroup) {
                    return;
                }

                this.tabGroup.selectTab(this.betColumnType.Betslip.toString());
            });

        this.vm$ = combineLatest([
            this.uiStates$.pipe(map((s) => s.includes(UiStates.EditBet))).pipe(distinctUntilChanged()),
            this.betslipIntegrationService.betslipPicksAndLegsCount$().pipe(distinctUntilChanged()),
            this.myBetsSummary.summaryUpdate.pipe(
                startWith(this.myBetsSummary.summary),
                map((summary) => this.getOpenBetCount(summary)),
                distinctUntilChanged(),
            ),
            this.routerEventsService.currentActivationEnd.pipe(
                map((event) => event?.snapshot?.data?.tag === RouteTag.MyBets),
                distinctUntilChanged(),
            ),
        ]).pipe(
            map(([isEditBet, betslipPickCount, myBetsCount, hideMyBets]) => {
                if (hideMyBets) {
                    this.active.set(this.betColumnType.Betslip.toString());
                }

                return {
                    isEditBet,
                    betslipPickCount,
                    myBetsCount,
                    hideMyBets,
                };
            }),
        );
    }

    tabSelected(name: string): void {
        this.active.set(name);
        if (name === BetColumnType.MyBets.toString()) {
            this.trackingService.track(trackingConstants.EVENT_NAVIGATION_MENUS, {
                [trackingConstants.PAGE_NAVIGATION_MENUS]: `MiniMybets_MyBets`,
            });

            // Clear the betslip results when user selects the Mybets tab before closing the confirm window.
            this.betslipBaseState$.pipe(take(1)).subscribe((state) => {
                switch (state) {
                    case BetslipState.Result:
                        this.store.dispatch(BetslipResultActions.close());
                        break;
                    case BetslipState.Edit:
                        this.store.dispatch(DigitalSettingsActions.closeSettings());
                        break;
                }
            });
        }

        this.dispatcher.dispatch('BET_COLUMNTYPE_TABSELECTED', name);
    }

    private getOpenBetCount(stats: OpenBetsSummary): string | undefined {
        if (!this.mybetsConfig.isOpenSummaryLoadingRestricted) {
            if (this.mybetsConfig.isShowOpenBetsCount) {
                return stats.openBetsCount > 99 ? '99+' : stats.openBetsCount.toString();
            } else {
                return stats.liveBetsCount.toString() || undefined;
            }
        }
        return undefined;
    }
}
